import React from "react";
import styled from "styled-components";
import { bot_icon, lxp_thumbnail, aiform_thumbnail, lxp_benefits, forms_icon, lms_icon } from "../../../assets/images";
import { FaArrowRightLong } from "react-icons/fa6";


const ProductsLanding = () => {
  const cards = [
    {
      id: "lms",
      title: "LXP",
      subtitle: "Next-Gen Teaching,\nAI-Powered",
      tone: "sky",
      image: lxp_thumbnail,
      href: "https://ailms.campusinsights.ai",
      external: true, // Use external link
      icon: lms_icon,
    },
    {
      id: "forms",
      title: "Smart Forms",
      subtitle: "Faster Forms,\nZero Errors",
      tone: "lavender",
      image: aiform_thumbnail,
      href: "/products/smart-forms",
      icon: forms_icon,
    },
    {
      id: "assistant",
      title: "AI Assistant",
      subtitle: "24/7 Student\nEngagement",
      tone: "sky",
      image: lxp_benefits,
      href: "/products/ai-assistant",
      icon: bot_icon,
    },
  ];

  const [activeId, setActiveId] = React.useState(null);
  const toggleActive = (id) => setActiveId((cur) => (cur === id ? null : id));

  return (
    <Section>
      <Container>
        <Header>
          <Title>Campus Insights Portfolios</Title>
        </Header>

        <CardsRow>
          {cards.map((c) => (
            <CardWrap
              key={c.id}
              $tone={c.tone}
              className={activeId === c.id ? "active" : ""}
              onClick={() => toggleActive(c.id)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleActive(c.id);
                }
              }}
              aria-pressed={activeId === c.id}
            >
              <Inner>
                {/* Normal state content */}
                <Normal>
                  <IconDot $tone={c.tone}>
                  <img src={c.icon} alt={`${c.title} icon`} /></IconDot>
                  <NormalTitle>{c.title}</NormalTitle>
                  <NormalSub>{c.subtitle}</NormalSub>
                </Normal>

                {/* Hover / Active overlay */}
                <HoverLayer>
                  <Hero $img={c.image} aria-hidden="true" alt=" illustration" />
                  <HoverBody>
                    <HoverTitle>{c.title}</HoverTitle>
                    <HoverSub>{c.subtitle}</HoverSub>
                    <Cta
                      href={c.href}
                      target={c.external ? "_blank" : undefined}
                      rel={c.external ? "noopener noreferrer" : undefined}
                      // only stop bubbling (DO NOT preventDefault)
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        // Space does not activate <a> by default; map it to click
                        if (e.key === ' ') {
                          e.preventDefault();
                          e.currentTarget.click();
                        }
                        // Enter already activates <a>; don't block it
                        e.stopPropagation(); // guard against parents hijacking keydown
                      }}
                    >
                      <span>Learn More</span>
                      <FaArrowRightLong />
                    </Cta>
                  </HoverBody>
                </HoverLayer>
              </Inner>
            </CardWrap>
          ))}
        </CardsRow>
      </Container>
    </Section>
  );
};

export default ProductsLanding;

/* ================== styles ================== */

const Section = styled.section`
  background: #eff4ff;
  display: flex;
  justify-content: center;
  padding: 64px 20px 80px;
  font-family: Inter, -apple-system, Roboto, Helvetica, Arial, sans-serif;
  @media (max-width: 640px) {
  padding: 43px 20px 49px;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1100px;
`;

const Header = styled.header`
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(28px, 3.2vw, 44px);
  line-height: 1.2;
  font-weight: 700;
  color: #111827;
`;

const CardsRow = styled.div`
  margin-top: 36px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 26px;

  @media (max-width: 960px) {
    // grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CardWrap = styled.article`
  --pad: 14px;
  --outerR: 22px;
  --innerR: 16px;
  --h: 310px;

  height: var(--h);
  border-radius: var(--outerR);
  padding: var(--pad);
  transition: transform 220ms ease, box-shadow 220ms ease, background 220ms ease;

  background: ${({ $tone }) =>
    $tone === "lavender" ? "#DBE0FF" : "#C6E8FF"};
  box-shadow: 0 1px 0 rgba(16, 24, 40, 0.06);

  &:hover,
  &.active,
  &:focus-within {
    background: linear-gradient(180deg, #6d59ff 0%, #8C80EB 100%); /* purple frame */
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(16, 24, 40, 0.12);
  }
    
  @media(max-width: 640px) {
  --h: 155px;
  }
`;

const Inner = styled.div`
  position: relative;
  height: 100%;
  border-radius: var(--innerR);
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(17, 17, 26, 0.06) inset;
`;

/* ---------- Normal state ---------- */
const Normal = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  // padding: 30px 20px;
  gap: 12px;
  transition: opacity 180ms ease 40ms;

  ${CardWrap}:hover &,
  ${CardWrap}.active &,
  ${CardWrap}:focus-within & {
    opacity: 0;
    pointer-events: none;
  }
`;

const IconDot = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ $tone }) => 
    $tone === 'lavender' ? `linear-gradient(135deg, #7657ffff 0%, #937BFD 100%)` :
                           `linear-gradient(135deg, #5a7ffb 0%, #0097ff 100%)`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: #fff;
  box-shadow: 0 3px 8px rgba(16, 24, 40, 0.08);
      
  @media(max-width: 640px) {
  width: 52px;
  height: 52px;
  }
`;

const NormalTitle = styled.h3`
  margin: 6px 0 2px;
  font-weight: 700;
  font-size: 24px;
  color: #0b1220;
  letter-spacing: -0.3px;
  @media(max-width: 640px) {
  font-size: 20px;
  }
`;

const NormalSub = styled.p`
  margin: 0;
  color: #1e1e1e;
  opacity: 0.8;
  font-size: 14px;
  line-height: 1.4;
  // white-space: pre-line;
`;

/* ---------- Hover/Active state ---------- */
const HoverLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;

  opacity: 0;
  transform: translateY(6px);
  transition: opacity 220ms ease, transform 220ms ease;
  pointer-events: none;

  ${CardWrap}:hover &,
  ${CardWrap}.active &,
  ${CardWrap}:focus-within & {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const Hero = styled.div`
  height: 180px; /* fixed so total size stays constant */
  background: ${({ $img }) =>
    $img ? `url(${$img}) center/cover no-repeat` : "transparent"};

  @media(max-width: 640px) {
    display: none;
  }
`;

const HoverBody = styled.div`
  flex: 1;
  background: linear-gradient(180deg, #604bf9 0%, #412ad4 100%);
  color: #fff;
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HoverTitle = styled.h3`
  margin: 0;
  font-weight: 800;
  font-size: 22px;
  color: #ffffff;
`;

const HoverSub = styled.p`
  margin: 0;
  // white-space: pre-line;
  opacity: 0.95;
  line-height: 1.35;
`;

const Cta = styled.a`
  margin-top: 14px;
  align-self: flex-start;
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  padding: 12px 0;
  border-radius: 12px;
  background: #d6ecff;
  color: #0b1220;
  text-decoration: none;
  font-weight: 600;
  box-shadow: 0 1px 0 rgba(16, 24, 40, 0.05);

  opacity: 0;
  transform: translateY(8px);
  transition: opacity 220ms ease, transform 220ms ease;

  ${CardWrap}:hover &,
  ${CardWrap}.active &,
  ${CardWrap}:focus-within & {
    opacity: 1;
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #ffffffff;
    outline-offset: 2px;
  }
`;

const Arrow = styled.span`
  font-size: 16px;
  line-height: 1;
`;
